import * as React from "react";
import { DashboardsContext } from "@app/admin/contexts/DashboardsContext";
import { orderBy } from "lodash";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { ClientContext } from "@app/admin/contexts/ClientContext";

interface ClientDomainsAndDashboardsProps {
  disabled?: boolean;
}

export const ClientDomainsAndDashboards: React.FC<React.PropsWithChildren<ClientDomainsAndDashboardsProps>> = (props: ClientDomainsAndDashboardsProps) => {
  const [tempDomain, setTempDomain] = React.useState("");
  const [domainRemoved, setDomainRemoved] = React.useState(false);
  const dashboardList = React.useContext(DashboardsContext).dashboards;
  const clientContext = React.useContext(ClientContext);
  const newDomainRef = React.useRef<HTMLInputElement>(null);

  const handleDomainKeyPress = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter" && tempDomain !== "" && !clientContext.authorizedDomains.includes(tempDomain)) {
      authorizeDomain();
    }
  }

  const authorizeDomain = () => {
    const newDomains = clientContext.authorizedDomains.concat(newDomainRef.current.value.toLowerCase());
    clientContext.setAuthorizedDomains(newDomains.sort());
    setTempDomain("");
  }

  const unauthorizeDomain = (key: string) => {
    if (props.disabled) {
      return;
    }
    const newDomains = [...clientContext.authorizedDomains];
    newDomains.splice(newDomains.indexOf(key), 1);
    clientContext.setAuthorizedDomains(newDomains);
    setDomainRemoved(true);
  }

  const selectedDashboards = React.useMemo(() => {
    return clientContext.authorizedDashboards.map((d) => d.name);
  }, [clientContext.authorizedDashboards]);

  const dashboardOptions: { [key: string]: MultiselectItem } = React.useMemo(() => {
    const dashboards: { [key: string]: MultiselectItem } = {};
    dashboardList.forEach((d) => Object.assign(dashboards,
      {
        [d.name]: {
          currentState: selectedDashboards.find((sd) => sd === d.name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: d.name,
        }
      }
    ));
    return dashboards;
  }, [dashboardList, selectedDashboards]);


  const updateDashboards = (response: MultiselectOnChangeResponse) => {
    const selectedDashboards = response.checkedKeys;
    const selectedDashboardModels = selectedDashboards.map((d) => {
      const match = dashboardList.find((dl) => dl.name === d);
      return {
        name: match.name,
        type: match.type,
      }
    });
    clientContext.setAuthorizedDashboards(orderBy(selectedDashboardModels, (s) => s.name, "asc"));
  }

  return (
    <>
      <PageSection>
        <div className="mb-3">
          <div className="row container">
            <QESTextInput
              className="text-lowercase"
              containerClassName="col-8"
              label="Authorized Domains"
              disabled={props.disabled}
              testId="new-client__domains"
              value={tempDomain}
              onChange={(e) => setTempDomain(e.target.value.toLowerCase())}
              onKeyPress={handleDomainKeyPress}
              qesRef={newDomainRef}
              required={true}
              errorText="At least one domain must be added."
              isValid={clientContext.validationObject["authorizedDomains"]}
              wasFocusedExternally={domainRemoved}
            />
            <QESButton
              qesType="secondary"
              qesTestId="new-client__add-domain"
              className="col-2"
              onClick={authorizeDomain}
              disabled={!newDomainRef.current || newDomainRef.current.value === "" || clientContext.authorizedDomains.includes(newDomainRef.current?.value.toLowerCase()) || props.disabled}
            >
              Add
            </QESButton>
          </div>
          <div className="col-8 px-2 ms-1">
            <div className="ps-1 pe-1">
              {clientContext.authorizedDomains.length > 0 ?
                clientContext.authorizedDomains.map((d, index) => (
                  <div key={d} className="waffles-filters__filter mt-2 mb-2 d-flex justify-content-between align-items-center">
                    {d} <QESButton qesType="none" className="p-0" qesTestId={`client-domains-and-dashboards__unauthorize--${index}`} onClick={() => unauthorizeDomain(d)}><QesIcon iconName={QesIcons.XCircle} description="Unauthorize domain" /></QESButton>
                  </div>
                )) :
                (
                  <p>No domains have been authorized for this client.</p>
                )
              }
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection>
        <div className="col-12">
          <QESMultiSelect
            containerClassName="mb-3 px-2"
            disabled={props.disabled}
            errorText="At least one dashboard must be selected."
            label="Authorized Dashboards"
            onChange={updateDashboards}
            options={dashboardOptions}
            required={true}
            testId="new-client__dashboards"
            isValid={clientContext.validationObject["authorizedDashboards"]}
          />
        </div>
      </PageSection>
    </>
  );
}
